import React, { memo } from 'react';
import { useSound } from '../contexts/SoundContext';

const StarRating = memo(({ 
  value, 
  onChange, 
  maxStars = 6, 
  minStars = 1,
  size = "medium",
  disabled = false,
  label = ""
}) => {
  const { playInterfaceSelectionHover } = useSound();

  const sizeClasses = {
    small: "w-4 h-4",
    medium: "w-6 h-6", 
    large: "w-8 h-8"
  };

  const handleStarClick = (starValue) => {
    if (!disabled && onChange) {
      playInterfaceSelectionHover();
      onChange(starValue);
    }
  };

  const handleKeyDown = (event, starValue) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleStarClick(starValue);
    }
  };

  return (
    <div className="flex flex-col items-center">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
          {label}
        </label>
      )}
      <div className="flex items-center space-x-1" role="radiogroup" aria-label={label}>
        {[...Array(maxStars)].map((_, index) => {
          const starValue = index + 1;
          const isFilled = starValue <= value;
          const isBelowMin = starValue < minStars;
          const isActive = !disabled && !isBelowMin;
          const isCurrentValue = starValue === value;
          
          return (
            <button
              key={starValue}
              type="button"
              className={`
                ${sizeClasses[size]} 
                transition-all duration-200 
                ${isFilled ? 'text-yellow-500' : 'text-gray-300'} 
                ${isBelowMin ? 'opacity-60 cursor-not-allowed' : ''}
                ${isActive ? 'hover:text-yellow-400 hover:scale-110 cursor-pointer' : 'cursor-default'}
                ${disabled ? 'opacity-50' : ''}
                focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2
              `}
              onClick={() => !isBelowMin && handleStarClick(starValue)}
              onKeyDown={(e) => !isBelowMin && handleKeyDown(e, starValue)}
              aria-label={`${starValue} star${starValue !== 1 ? 's' : ''}${isBelowMin ? ' (not available)' : ''}`}
              aria-checked={starValue === value}
              aria-disabled={isBelowMin}
              role="radio"
              tabIndex={disabled || isBelowMin ? -1 : 0}
            >
              <svg
                fill="currentColor"
                viewBox="0 0 20 20"
                className="w-full h-full"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </button>
          );
        })}
      </div>
      <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
        {value}★
      </div>
    </div>
  );
});

StarRating.displayName = 'StarRating';

export default StarRating;
